import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {FilterService} from '../../../services/filter.service';
import {UntypedFormBuilder} from '@angular/forms';
import {IFilterTypes, IFilters} from '../../../interfaces/filters.interface';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {IAdvancedFilters} from 'src/app/interfaces/filters-post.interface';
import {Utils} from '../../utils/utils';

/**
 * This component get from the filter service array with type of inputs ( checkbox, input, date, doubleDate) and display it
 * using filter-multi-select, autocomplete-field components and data pickers. Also, filter collect data from user using elements
 * describe above and on every change emit this data to the father component
 */
@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export class FilterComponent implements OnInit {
  @Output() emitFilterObj = new EventEmitter();
  @Input() set filtersData(value: IFilters[]) {
    this.filters = value;
  }
  @Input() set filterSavedValues(value: IAdvancedFilters) {
    if (value) {
      this.formFilter.patchValue(value);
      this.isFilterApplied = !Utils.isEqualObjects(
        this.defaultAdvancedFilters,
        value
      );
      this.countFilters();
    }
  }
  isFilterApplied = false;
  defaultAdvancedFilters: IAdvancedFilters = {
    position: null,
    gender: null,
    level: null,
    projectManager: null,
    deliveryManager: null,
    unit: null,
    projects: null,
    assignedHr: null,
    assignedMentor: null,
    probationEndDate: null,
    probationEndStatus: null,
    personalData: null,
    techStack: null,
    framework: null,
    techInterviewStatus: null,
    techInterviewDate: null,
    englishLevel: null,
    englishCourses: null,
    securityTraining: null,
    internalCourses: null,
    conference: null,
    certifications: null,
    presentation: null,
    isAMentor: null,
    performanceReviewInThePeriod: {
      start: null,
      end: null,
    },
    promotionInThePeriod: {
      start: null,
      end: null,
    },
  };
  count = 0;
  doubleDateKeys = ['promotionInThePeriod', 'performanceReviewInThePeriod'];
  /**
   * List of filters to display. Come from server
   */
  filters: IFilters[] = [];
  showHideFilter = false;

  /**
   * manage display of date/doubleDate types of filter element
   */
  showHideObj = {
    probationEndDate: false,
    techInterviewDate: false,
    performanceReviewInThePeriod: false,
    promotionInThePeriod: false,
  };
  filterTypes!: IFilterTypes;
  @ViewChild('filter') filter!: ElementRef;
  constructor(
    private filterService: FilterService,
    private formBuilder: UntypedFormBuilder
  ) {}

  /**
   * Get filters object from filter service
   * when formFilter value change, emit this value to the father component
   */

  ngOnInit(): void {
    this.filterTypes = this.filterService.filterTypes;
    this.formFilter.valueChanges
      .pipe(debounceTime(700), distinctUntilChanged())
      .subscribe(() => {
        this.isFilterApplied = !Utils.isEqualObjects(
          this.defaultAdvancedFilters,
          this.formFilter.value
        );
        this.countFilters();
        this.emitFilterObj.emit(this.formFilter.value);
      });
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    const target = event.target as HTMLInputElement;
    const dataPickerNativeElement = document.getElementsByClassName(
      'flatpickr-calendar open'
    )[0];
    if (this.showHideFilter) {
      if (dataPickerNativeElement) {
        this.showHideFilter = !!(
          this.filter.nativeElement.contains(target) ||
          dataPickerNativeElement.contains(target) ||
          target.tagName === 'OPTION' ||
          target.tagName === 'BODY'
        );
      } else {
        this.showHideFilter = !!(
          this.filter.nativeElement.contains(target) ||
          target.tagName === 'OPTION' ||
          target.classList.contains('remove-filter') ||
          target.tagName === 'BODY'
        );
      }
    }
  }

  formFilter = this.formBuilder.group({
    position: [],
    gender: [],
    level: [],
    projectManager: [],
    deliveryManager: [],
    unit: [],
    projects: [],
    assignedHr: [],
    assignedMentor: [],
    probationEndDate: [],
    probationEndStatus: [],
    personalData: [],
    techStack: [],
    framework: [],
    techInterviewStatus: [],
    techInterviewDate: [],
    englishLevel: [],
    englishCourses: [],
    securityTraining: [],
    internalCourses: [],
    conference: [],
    certifications: [],
    presentation: [],
    isAMentor: [],
    performanceReviewInThePeriod: this.formBuilder.group({
      start: [],
      end: [],
    }),
    promotionInThePeriod: this.formBuilder.group({
      start: [],
      end: [],
    }),
  });

  /**
   * This function calls when user clicked one of the date or doubleDate title and get name of the filter which was clicked.
   * Depending on name which comes will be changed showHideObj property to the opposite boolean value and return this value
   *
   * @param { string } name - property of filter object
   * @return { boolean } one of the showHideObj object property
   */

  showHideFunc(name: string): boolean {
    switch (name) {
      case 'probationEndDate': {
        this.showHideObj.probationEndDate = !this.showHideObj.probationEndDate;
        return this.showHideObj.probationEndDate;
      }
      case 'techInterviewDate': {
        this.showHideObj.techInterviewDate =
          !this.showHideObj.techInterviewDate;
        return this.showHideObj.techInterviewDate;
      }
      case 'performanceReviewInThePeriod': {
        this.showHideObj.performanceReviewInThePeriod =
          !this.showHideObj.performanceReviewInThePeriod;
        return this.showHideObj.performanceReviewInThePeriod;
      }
      case 'promotionInThePeriod': {
        this.showHideObj.promotionInThePeriod =
          !this.showHideObj.promotionInThePeriod;
        return this.showHideObj.promotionInThePeriod;
      }
      default: {
        return false;
      }
    }
  }

  clearFilters(): void {
    this.formFilter.reset();
    this.isFilterApplied = false;
  }

  countFilters(): void {
    let count = 0;
    const obj = this.formFilter.value;
    for (const key in obj) {
      if (this.doubleDateKeys.includes(key)) {
        if (obj[key].start || obj[key].end) {
          count++;
        }
      } else if (obj.hasOwnProperty(key) && obj[key]) {
        count++;
      }
    }
    this.count = count;
  }

  clearDate(name: string): void {
    this.formFilter.controls[name]?.setValue(null);
  }

  clearDates(name: string): void {
    this.formFilter.controls[name]?.setValue({
      start: null,
      end: null,
    });
  }

  countDobleDateFilters(value: {
    start: string | null;
    end: string | null;
  }): number {
    return value ? Number(!!value.start) + Number(!!value.end) : 0;
  }
}
